/**
 * Fallback <variant-selects> element.
 *
 * Install as assets/variant-selects-fallback.js and load it from
 * snippets/product-variant-picker.liquid (see the install steps).
 *
 * On every option change it:
 *  1. finds the matching variant from the JSON inside <variant-selects>
 *  2. writes the variant ID into every input[name="id"] owned by the product form
 *  3. updates ?variant= in the address bar (unless data-update-url="false")
 *  4. enables/disables the Add to cart button
 *  5. refreshes the price + button text via the Section Rendering API (best effort)
 *  6. updates the sold-out / non-existent state of the other options
 *
 * It only defines the element if the theme hasn't already done so.
 */
if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this.requestId = 0;
        this.onChange = this.onChange.bind(this);
      }

      connectedCallback() {
        this.addEventListener('change', this.onChange);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
      }

      /* ---------- data helpers ---------- */

      optionsOf(variant) {
        return variant.options || [variant.option1, variant.option2, variant.option3].filter((o) => o != null);
      }

      getVariants() {
        if (this._variants) return this._variants;
        for (const script of this.querySelectorAll('script[type="application/json"]')) {
          try {
            const data = JSON.parse(script.textContent);
            if (Array.isArray(data) && data.length && data[0].id !== undefined) {
              this._variants = data;
              return data;
            }
          } catch (e) {
            /* not the variants JSON (e.g. sizing chart data), keep looking */
          }
        }
        return [];
      }

      getGroups() {
        return [...this.querySelectorAll(':scope > .product-form__input')];
      }

      getSelectedOptions() {
        return this.getGroups().map((group) => {
          const select = group.querySelector('select[name^="options["]');
          if (select) return select.value;
          const checked = group.querySelector('input[type="radio"]:checked');
          return checked ? checked.value : null;
        });
      }

      /**
       * Exact match if possible. Otherwise keep the options up to and including the
       * one the customer just changed, and pick the best variant for the rest
       * (available first).
       */
      findVariant(selected, changedIndex) {
        const variants = this.getVariants();
        const matches = (variant, count) => {
          const options = this.optionsOf(variant);
          return selected.slice(0, count).every((value, i) => options[i] === value);
        };

        const exact = variants.find((v) => matches(v, selected.length));
        if (exact) return exact;

        for (let count = selected.length - 1; count >= Math.max(changedIndex + 1, 1); count--) {
          const candidates = variants.filter((v) => matches(v, count));
          if (candidates.length) return candidates.find((v) => v.available) || candidates[0];
        }
        return null;
      }

      /* ---------- event handling ---------- */

      onChange(event) {
        const target = event.target;
        if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

        const changedIndex = this.getGroups().findIndex((group) => group.contains(target));
        if (changedIndex === -1) return; // an input that isn't one of the option pickers

        const variant = this.findVariant(this.getSelectedOptions(), changedIndex);
        if (!variant) return;

        const options = this.optionsOf(variant);
        this.syncInputs(options);
        this.updateOptionStates(options);
        this.updateUrl(variant);
        this.updateForm(variant);
        this.refreshSection(variant);

        this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
      }

      /** Make the pickers reflect the variant we ended up on. */
      syncInputs(options) {
        this.getGroups().forEach((group, i) => {
          const select = group.querySelector('select[name^="options["]');
          if (select) {
            select.value = options[i];
            return;
          }
          group.querySelectorAll('input[type="radio"]').forEach((radio) => {
            radio.checked = radio.value === options[i];
          });
        });
      }

      /** Same rules the Liquid uses on first render: non-existent / unavailable / disabled. */
      updateOptionStates(options) {
        const variants = this.getVariants();
        const skipUnavailable = this.dataset.skipUnavailable === 'true';

        this.getGroups().forEach((group, i) => {
          const prefix = options.slice(0, i);
          const candidates = variants.filter((v) => {
            const vo = this.optionsOf(v);
            return prefix.every((value, j) => vo[j] === value);
          });

          group.querySelectorAll('input[type="radio"], select[name^="options["] > option').forEach((item) => {
            const matching = candidates.filter((v) => this.optionsOf(v)[i] === item.value);
            const exists = matching.length > 0;
            const available = matching.some((v) => v.available);

            item.classList.toggle('non-existent', !exists);
            item.classList.toggle('unavailable', exists && !available);

            if (item.tagName === 'INPUT') {
              item.classList.toggle('disabled', !available);
            } else {
              const hidden = !exists || (!available && skipUnavailable);
              item.hidden = hidden;
              item.disabled = hidden;
            }
          });
        });
      }

      updateUrl(variant) {
        if (this.dataset.updateUrl === 'false' || !this.dataset.url) return;

        // Only touch the address bar when we're actually on this product's page.
        const handle = this.dataset.url.split('?')[0].split('/').filter(Boolean).pop();
        const currentHandle = window.location.pathname.split('/').filter(Boolean).pop();
        if (handle !== currentHandle) return;

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      getForm() {
        return document.getElementById(`product-form-${this.dataset.section}`);
      }

      /** The part that fixes "always adds the default variant". */
      updateForm(variant) {
        const form = this.getForm();
        if (!form) return;

        document.querySelectorAll('input[name="id"]').forEach((input) => {
          if (input.form !== form) return;
          input.value = variant.id;
          input.disabled = false;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        [...form.elements]
          .filter((el) => el.type === 'submit' && el.name === 'add')
          .forEach((button) => {
            button.disabled = !variant.available;
          });
      }

      /** Best effort: pull the new price and button text from the server-rendered section. */
      async refreshSection(variant) {
        const requestId = ++this.requestId;
        const section = this.dataset.section;
        const original = this.dataset.originalSection || section; // quick-add renames IDs

        try {
          const response = await fetch(`${this.dataset.url}?variant=${variant.id}&section_id=${original}`);
          if (!response.ok) return;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (requestId !== this.requestId) return; // a newer change superseded this one

          const newPrice = html.getElementById(`price-${original}`);
          const price = document.getElementById(`price-${section}`);
          if (newPrice && price) price.innerHTML = newPrice.innerHTML;

          const newForm = html.getElementById(`product-form-${original}`);
          const form = this.getForm();
          const newButton = newForm && newForm.querySelector('[name="add"]');
          const button = form && form.querySelector('[name="add"]');
          if (newButton && button) {
            button.innerHTML = newButton.innerHTML;
            button.disabled = newButton.hasAttribute('disabled');
          }
        } catch (error) {
          /* cosmetic only, the variant ID is already in the form */
        }
      }
    }
  );
}